import { BaseAPI } from './BaseAPI';
import {
  ValAddress,
  Denom,
  ExchangeRateVote,
  Coin,
  Coins,
  ExchangeRatePrevote,
  AccAddress,
  Dec,
} from '../../../core';

export interface OracleParams {
  vote_period: number;
  vote_threshold: Dec;
  reward_band: Dec;
  reward_distribution_window: number;
  whitelist: Denom[];
  slash_fraction: Dec;
  slash_window: number;
  min_valid_per_window: Dec;
}

export namespace OracleParams {
  export interface Data {
    vote_period: string;
    vote_threshold: string;
    reward_band: string;
    reward_distribution_window: string;
    whitelist: Denom[];
    slash_fraction: string;
    slash_window: string;
    min_valid_per_window: string;
  }
}

export class OracleAPI extends BaseAPI {
  public async votes(
    denom?: Denom,
    validator?: ValAddress
  ): Promise<ExchangeRateVote[]> {
    if (validator !== undefined && denom !== undefined) {
      let vote = await this.c.get<ExchangeRateVote.Data>(
        `/oracle/denoms/${denom}/votes/${validator}`
      );
      return [ExchangeRateVote.fromData(vote.result)];
    } else if (validator !== undefined) {
      let votes = await this.c.get<ExchangeRateVote.Data[]>(
        `/oracle/voters/${validator}/votes`
      );
      return votes.result.map(ExchangeRateVote.fromData);
    } else if (denom !== undefined) {
      let votes = await this.c.get<ExchangeRateVote.Data[]>(
        `/oracle/denoms/${denom}/votes`
      );
      return votes.result.map(ExchangeRateVote.fromData);
    } else {
      throw new Error('both denom and validator cannot both be undefined');
    }
  }

  public async prevotes(
    denom?: Denom,
    validator?: ValAddress
  ): Promise<ExchangeRatePrevote[]> {
    if (validator !== undefined && denom !== undefined) {
      let prevote = await this.c.get<ExchangeRatePrevote.Data>(
        `/oracle/denoms/${denom}/prevotes/${validator}`
      );
      return [ExchangeRatePrevote.fromData(prevote.result)];
    } else if (validator !== undefined) {
      let prevotes = await this.c.get<ExchangeRatePrevote.Data[]>(
        `/oracle/voters/${validator}/prevotes`
      );
      return prevotes.result.map(ExchangeRatePrevote.fromData);
    } else if (denom !== undefined) {
      let prevotes = await this.c.get<ExchangeRatePrevote.Data[]>(
        `/oracle/denoms/${denom}/prevotes`
      );
      return prevotes.result.map(ExchangeRatePrevote.fromData);
    } else {
      throw new Error('both denom and validator cannot both be undefined');
    }
  }

  public async exchangeRates(): Promise<Coins> {
    return this.c
      .get<Coins.Data>(`/oracle/denoms/exchange_rates`)
      .then(d => Coins.fromData(d.result));
  }

  public async exchangeRate(denom: Denom): Promise<Coin | undefined> {
    const rates = await this.exchangeRates();
    return rates.get(denom);
  }

  public async activeDenoms(): Promise<Denom[]> {
    return this.c.get<Denom[]>(`/oracle/denoms/actives`).then(d => d.result);
  }

  public async feederAddress(validator: ValAddress): Promise<AccAddress> {
    return this.c
      .get<AccAddress>(`/oracle/voters/${validator}/feeder`)
      .then(d => d.result);
  }

  public async misses(validator: ValAddress): Promise<number> {
    return this.c
      .get<string>(`/oracle/voters/${validator}/miss`)
      .then(d => Number.parseInt(d.result));
  }

  public async parameters(): Promise<OracleParams> {
    return this.c
      .get<OracleParams.Data>(`/oracle/parameters`)
      .then(d => d.result)
      .then(d => ({
        vote_period: Number.parseInt(d.vote_period),
        vote_threshold: new Dec(d.vote_threshold),
        reward_band: new Dec(d.reward_band),
        reward_distribution_window: Number.parseInt(
          d.reward_distribution_window
        ),
        whitelist: d.whitelist,
        slash_fraction: new Dec(d.slash_fraction),
        slash_window: Number.parseInt(d.slash_window),
        min_valid_per_window: new Dec(d.min_valid_per_window),
      }));
  }
}
